import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  setDoc,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { useNavigate } from "react-router-dom";
import DeliveryChargeEditor from "../../components/DeliveryChargeEditor";

const AdminCustomOrdersEnhanced = () => {
  const [orders, setOrders] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [partnerInputs, setPartnerInputs] = useState({});
  const [confirmOrderId, setConfirmOrderId] = useState(null);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const orderQuery = query(collection(db, "custom_orders"), where("status", "==", "pending"));
      const orderSnap = await getDocs(orderQuery);
      const ordersData = orderSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

      const partnerQuery = query(collection(db, "users"), where("role", "==", "deliveryPartner"));
      const partnerSnap = await getDocs(partnerQuery);
      const partnersData = partnerSnap.docs.map((doc) => ({ uid: doc.id, ...doc.data() }));

      setOrders(ordersData);
      setPartners(partnersData);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load data. Check permissions.");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  const assignToPartner = async (order, partnerId) => {
    const deliveryId = `${order.userId}_${Date.now()}`;
    await setDoc(doc(db, "deliveries", deliveryId), {
      date: new Date().toISOString().slice(0, 10),
      userId: order.userId,
      customOrderId: order.id,
      assignedPartnerId: partnerId,
      status: "pending",
      slot: order.slot || "Morning",
      items: order.items || [{ name: `Rotis x${order.qty}` }],
      address: order.addressDetails || "N/A",
      area: order.area || "N/A",
      createdAt: new Date(),
    });
    await updateDoc(doc(db, "custom_orders", order.id), {
      status: "assigned",
    });
    alert("✅ Order assigned");
    setPartnerInputs((prev) => ({ ...prev, [order.id]: "" }));
    setConfirmOrderId(null);
    await fetchData(); // Refresh the list
  };

  const handleDeliveryChargeUpdate = (updatedOrder) => {
    setOrders(prev => 
      prev.map(order => 
        order.id === updatedOrder.id ? updatedOrder : order
      )
    );
    setEditingOrderId(null);
  };

  const handleDeliveryChargeCancel = () => {
    setEditingOrderId(null);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-green-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
        <p className="text-green-700 font-medium">Loading custom orders...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-green-50">
      <div className="text-center">
        <div className="text-red-600 text-xl mb-4">❌</div>
        <p className="text-red-700 font-medium">{error}</p>
        <button 
          onClick={fetchData}
          className="mt-4 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
        >
          Retry
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-green-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-green-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-green-700">JR Home Made Foods</h1>
              <p className="text-gray-600">Enhanced Custom Orders Management</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-all"
              >
                🏠 Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6 max-w-7xl mx-auto">
        {/* Quick Actions Bar */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-green-100 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-green-700 mb-2">📩 Custom Orders Management</h2>
              <p className="text-gray-600">Manage pending custom orders and delivery charges</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {refreshing ? "🔄 Refreshing..." : "🔄 Refresh"}
              </button>
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
              >
                📊 Dashboard
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-xl shadow-sm border border-green-100">
            <div className="text-sm text-gray-600">Total Pending Orders</div>
            <div className="text-2xl font-bold text-green-700">{orders.length}</div>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-sm border border-green-100">
            <div className="text-sm text-gray-600">Available Partners</div>
            <div className="text-2xl font-bold text-blue-700">{partners.length}</div>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-sm border border-green-100">
            <div className="text-sm text-gray-600">Total Value</div>
            <div className="text-2xl font-bold text-purple-700">
              ₹{orders.reduce((sum, order) => sum + (order.total || 0), 0).toLocaleString()}
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-sm border border-green-100">
            <div className="text-sm text-gray-600">Avg Delivery Charge</div>
            <div className="text-2xl font-bold text-orange-700">
              ₹{orders.length > 0 ? (orders.reduce((sum, order) => sum + (order.deliveryCharge || 0), 0) / orders.length).toFixed(0) : 0}
            </div>
          </div>
        </div>

        {/* Orders List */}
        {orders.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-8 border border-green-100 text-center">
            <div className="text-4xl mb-4">📭</div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No Pending Custom Orders</h3>
            <p className="text-gray-600">All custom orders have been processed or assigned.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {orders.map((order) => (
              <div key={order.id} className="bg-white rounded-xl shadow-sm border border-green-100 overflow-hidden">
                {/* Order Header */}
                <div className="bg-green-50 px-6 py-4 border-b border-green-100">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-semibold text-green-800">
                        👤 {order.userName || order.userId || "Unnamed Customer"}
                      </h3>
                      <p className="text-sm text-gray-600">{order.userEmail}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">Order ID</div>
                      <div className="text-xs font-mono text-gray-400">{order.id.slice(-8)}</div>
                    </div>
                  </div>
                </div>

                {/* Order Details */}
                <div className="p-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-sm text-gray-500">📅 Date</div>
                      <div className="font-medium">{order.deliveryDate || "N/A"}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">📍 Area</div>
                      <div className="font-medium">{order.area || "N/A"}</div>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="mb-4">
                    <div className="text-sm text-gray-500">📍 Address</div>
                    <div className="font-medium text-sm">{order.addressDetails || "N/A"}</div>
                  </div>

                  {/* Items and Pricing */}
                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <strong className="text-gray-700">🍽 Items:</strong>
                      <span className="text-sm text-gray-600">{order.qty} rotis × ₹20</span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>Items Total:</span>
                        <span>₹{order.qty * 20}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Delivery Charge:</span>
                        <span className="font-medium">₹{order.deliveryCharge || 0}</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg border-t pt-1">
                        <span>Total:</span>
                        <span className="text-green-700">₹{order.total}</span>
                      </div>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="mb-4">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                      order.status === "pending" ? "bg-yellow-100 text-yellow-800" :
                      order.status === "assigned" ? "bg-blue-100 text-blue-800" :
                      "bg-green-100 text-green-800"
                    }`}>
                      {order.status || "pending"}
                    </span>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    {/* Delivery Charge Edit Button */}
                    <button
                      onClick={() => setEditingOrderId(order.id)}
                      className="w-full bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-all"
                    >
                      💰 Edit Delivery Charge
                    </button>

                    {/* Partner Assignment */}
                    <div className="space-y-2">
                      <select
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        value={partnerInputs[order.id] || ""}
                        onChange={(e) => setPartnerInputs((prev) => ({ ...prev, [order.id]: e.target.value }))}
                      >
                        <option value="">-- Select Delivery Partner --</option>
                        {partners.map((partner) => (
                          <option key={partner.uid} value={partner.uid}>
                            {partner.name || partner.uid} ({partner.area || "No area"})
                          </option>
                        ))}
                      </select>
                      <button
                        className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all"
                        onClick={() => setConfirmOrderId(order.id)}
                        disabled={!partnerInputs[order.id]}
                      >
                        🚚 Assign to Partner
                      </button>
                    </div>
                  </div>
                </div>

                {/* Delivery Charge Editor Modal */}
                {editingOrderId === order.id && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                      <DeliveryChargeEditor
                        order={order}
                        onUpdate={handleDeliveryChargeUpdate}
                        onCancel={handleDeliveryChargeCancel}
                      />
                    </div>
                  </div>
                )}

                {/* Confirmation Modal */}
                {confirmOrderId === order.id && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-sm">
                      <h3 className="text-lg font-semibold mb-4">Confirm Assignment</h3>
                      <p className="text-sm text-gray-700 mb-2">
                        Are you sure you want to assign this order to:
                      </p>
                      <p className="text-green-700 font-semibold mb-4">
                        {partners.find((p) => p.uid === partnerInputs[order.id])?.name || partnerInputs[order.id]}
                      </p>
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => setConfirmOrderId(null)}
                          className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => assignToPartner(order, partnerInputs[order.id])}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          Confirm
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCustomOrdersEnhanced; 